// The head + both eyeballs as ONE asset (head_eyes.glb + head_eyes.json).

import { fetchAssetArrayBuffer, fetchAssetJson } from '../assets/fetchAsset.ts'
import { vector3 } from '../math/index.ts'
import type { Vector3 } from '../math/index.ts'
import { EYE_SIDES } from './eyeSide.ts'
import type { EyeSide } from './eyeSide.ts'
import type { EyeShell } from './controller/index.ts'

export type EyeMeshPart = {
  local: Float32Array
  indices: Uint32Array
  uv: Float32Array
  // The exporter's outward-oriented normals; recomputing would re-derive from winding.
  normals: Float32Array | null
  pivot: Vector3
  meanCentre: Vector3
  axisOptical: Vector3
  axisFrame: Vector3
  poleIndex: number
  radiusMean: number
  sphereRadius: number
  count: number
}

export type HeadMeshPart = {
  positions: Float32Array
  indices: Uint32Array
  normals: Float32Array | null
  count: number
}

export type HeadEyeMesh = {
  head: HeadMeshPart
  eyes: Record<EyeSide, EyeMeshPart>
  sidecar: any
}

const NODE_NAME: Record<EyeSide, string> = { left: 'eye_left', right: 'eye_right' }
// The GLB node translation and the sidecar pivot describe the same point.
const PIVOT_TOLERANCE = 1e-6

const readVector = (value: unknown, what: string): Vector3 =>
  vector3.parse(value, `head_eyes sidecar: ${what}`)

function findMesh(root: any, name: string): any {
  let found: any = null
  root.traverse?.((object: any) => {
    if (!found && object.name === name && object.geometry) found = object
  })
  if (!found) throw new Error(`head_eyes.glb: no mesh node named '${name}'`)
  return found
}

function requireAttribute(mesh: any, name: string, itemSize: number, node: string): Float32Array {
  const attribute = mesh.geometry.getAttribute(name)
  if (!attribute) throw new Error(`head_eyes.glb: node '${node}' has no ${name} attribute`)
  if (attribute.itemSize !== itemSize) {
    throw new Error(`head_eyes.glb: node '${node}' ${name} itemSize ${attribute.itemSize} != ${itemSize}`)
  }
  if (!(attribute.array instanceof Float32Array)) {
    throw new Error(`head_eyes.glb: node '${node}' ${name} is ${attribute.array.constructor.name}, expected Float32Array`)
  }
  return attribute.array as Float32Array
}

// Optional attribute: absent is fine (the consumer can compute), wrong shape is not.
function optionalAttribute(mesh: any, name: string, itemSize: number, node: string): Float32Array | null {
  return mesh.geometry.getAttribute(name) ? requireAttribute(mesh, name, itemSize, node) : null
}

function indices(mesh: any, node: string): Uint32Array {
  const index = mesh.geometry.getIndex()
  if (!index) throw new Error(`head_eyes.glb: node '${node}' is not indexed`)
  return index.array instanceof Uint32Array ? (index.array as Uint32Array) : new Uint32Array(index.array)
}

// sha256 hex of a typed array's exact little-endian bytes, matching the exporter.
export async function sha256Hex(values: Float32Array): Promise<string> {
  const bytes = new Uint8Array(values.buffer, values.byteOffset, values.byteLength)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

// Synchronous and pure; hash checking is async, so loadHeadEyeMesh runs it separately.
export function buildHeadEyeMesh(gltfScene: any, sidecar: any): HeadEyeMesh {
  if (!sidecar?.eyes?.left || !sidecar?.eyes?.right || !sidecar?.head) {
    throw new Error('head_eyes sidecar: missing head/eyes entries')
  }

  const headNode = findMesh(gltfScene, 'head')
  const headPositions = requireAttribute(headNode, 'position', 3, 'head')
  if (headPositions.length / 3 !== sidecar.head.vertex_count) {
    throw new Error(`head_eyes: head has ${headPositions.length / 3} verts, `
      + `sidecar says ${sidecar.head.vertex_count}`)
  }
  const head: HeadMeshPart = {
    positions: headPositions,
    indices: indices(headNode, 'head'),
    normals: optionalAttribute(headNode, 'normal', 3, 'head'),
    count: headPositions.length / 3,
  }

  const eyes = {} as Record<EyeSide, EyeMeshPart>
  for (const side of EYE_SIDES) {
    const name = NODE_NAME[side]
    const meta = sidecar.eyes[side]
    const node = findMesh(gltfScene, name)
    const local = requireAttribute(node, 'position', 3, name)
    const count = local.length / 3
    if (count !== meta.vertex_count) {
      throw new Error(`head_eyes: ${name} has ${count} verts, sidecar says ${meta.vertex_count}`)
    }
    const uv = requireAttribute(node, 'uv', 2, name)
    if (uv.length / 2 !== count) throw new Error(`head_eyes: ${name} uv/position count mismatch`)

    const pivot = readVector(meta.pivot, `eyes.${side}.pivot`)
    const nodePivot: Vector3 = [node.position.x, node.position.y, node.position.z]
    for (let axis = 0; axis < 3; axis++) {
      if (Math.abs(nodePivot[axis] - pivot[axis]) > PIVOT_TOLERANCE) {
        throw new Error(`head_eyes: ${name} node translation ${JSON.stringify(nodePivot)} `
          + `disagrees with sidecar pivot ${JSON.stringify(pivot)} — mismatched glb/json pair?`)
      }
    }
    if (!(meta.pole_idx >= 0 && meta.pole_idx < count)) {
      throw new Error(`head_eyes: ${name} pole_idx ${meta.pole_idx} out of range`)
    }

    eyes[side] = {
      local,
      indices: indices(node, name),
      normals: optionalAttribute(node, 'normal', 3, name),
      uv,
      pivot,
      meanCentre: readVector(meta.mean_centre, `eyes.${side}.mean_centre`),
      axisOptical: vector3.normalize(readVector(meta.axis_optical, `eyes.${side}.axis_optical`)),
      axisFrame: vector3.normalize(readVector(meta.axis_frame, `eyes.${side}.axis_frame`)),
      poleIndex: meta.pole_idx,
      radiusMean: meta.radius_mean,
      sphereRadius: meta.sphere_radius,
      count,
    }
  }
  return { head, eyes, sidecar }
}

export async function verifyPositionHashes(mesh: HeadEyeMesh): Promise<void> {
  const parts: Array<[string, Float32Array, string]> = [
    ['head', mesh.head.positions, mesh.sidecar.head.position_sha256],
    ['eye_left', mesh.eyes.left.local, mesh.sidecar.eyes.left.position_sha256],
    ['eye_right', mesh.eyes.right.local, mesh.sidecar.eyes.right.position_sha256],
  ]
  for (const [name, values, expected] of parts) {
    if (!expected) throw new Error(`head_eyes sidecar: no position_sha256 for ${name}`)
    const actual = await sha256Hex(values)
    if (actual !== expected) {
      throw new Error(`head_eyes: ${name} POSITION hash ${actual.slice(0, 12)} != sidecar `
        + `${expected.slice(0, 12)} — the GLB was re-encoded and the vertex order may have moved`)
    }
  }
}

// Parses from bytes we fetched, so a test can use a local file on the same path.
export async function parseGlbScene(bytes: ArrayBuffer): Promise<any> {
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
  return new Promise((resolve, reject) => {
    new GLTFLoader().parse(bytes, '', (gltf: any) => resolve(gltf.scene), reject)
  })
}

export async function loadHeadEyeMesh(glbUrl: string, sidecarUrl: string): Promise<HeadEyeMesh> {
  const [bytes, sidecar] = await Promise.all([
    fetchAssetArrayBuffer(glbUrl, 'head_eyes'),
    fetchAssetJson(sidecarUrl, 'head_eyes'),
  ])
  return loadHeadEyeMeshFromBytes(bytes, sidecar)
}

// Bundle path: the GLB arrives as bytes from the /ogs pack list, not as a URL.
export async function loadHeadEyeMeshFromBytes(glb: ArrayBuffer | Uint8Array,
                                               sidecar: any): Promise<HeadEyeMesh> {
  const bytes = glb instanceof Uint8Array
    ? glb.buffer.slice(glb.byteOffset, glb.byteOffset + glb.byteLength) as ArrayBuffer
    : glb
  const mesh = buildHeadEyeMesh(await parseGlbScene(bytes), sidecar)
  await verifyPositionHashes(mesh)
  return mesh
}

/** The four fields the eye controller needs, per eye. */
export function eyeShellsFromMesh(mesh: HeadEyeMesh): Record<EyeSide, EyeShell> {
  const shells = {} as Record<EyeSide, EyeShell>
  for (const side of EYE_SIDES) {
    const part = mesh.eyes[side]
    shells[side] = {
      localVertices: part.local,
      pivot: part.pivot,
      opticalAxis: part.axisOptical,
      poleIndex: part.poleIndex,
    }
  }
  return shells
}
